import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Autocomplete, Card
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, AccountBalance as BankIcon
} from '@mui/icons-material';

const PaymentAllocation = () => {
    const [allocations, setAllocations] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);

    // Form State
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [basic, setBasic] = useState(0);
    const [hra, setHra] = useState(0);
    const [da, setDa] = useState(0);
    const [ta, setTa] = useState(0);
    const [paymentMode, setPaymentMode] = useState('Bank Transfer');
    const [bankName, setBankName] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [branch, setBranch] = useState('');
    const [ifsc, setIfsc] = useState('');

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            await fetchInitialData();
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchInitialData = async () => {
        const rolesToFetch = ["Admin", "Staff", "Reception", "Accountant", "Library", "Teacher", "Store", "Hostel", "Canteen", "Driver"];

        try {
            // Fetch directly from authroles collection without schoolId restrictions
            const staffQuery = query(
                collection(db, "authroles"),
                where("role", "in", rolesToFetch)
            );

            const staffSnap = await getDocs(staffQuery);

            const list = staffSnap.docs.map(doc => {
                const data = doc.data();
                const displayName = data.name || "No Name";

                return {
                    id: doc.id,
                    label: `${displayName} (${data.role})`.toUpperCase(),
                    name: displayName.toUpperCase(),
                    role: data.role
                };
            });

            setStaffList(list);
            await fetchAllocations();
        } catch (error) {
            console.error("Error fetching staff data:", error);
        }
    };

    const fetchAllocations = async () => {
        try {
            const q = query(collection(db, "salaryAllocations"));
            const snap = await getDocs(q);
            setAllocations(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Error fetching allocations:", error);
        }
    };

    const totalSalary = Number(basic) + Number(hra) + Number(da) + Number(ta);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStaff) return alert("Please select staff");
        setSaving(true);

        const payload = {
            staffId: selectedStaff.id,
            staffName: selectedStaff.name,
            basic: Number(basic),
            hra: Number(hra),
            da: Number(da),
            ta: Number(ta),
            totalSalary,
            paymentMode,
            bankName: paymentMode === 'Bank Transfer' ? bankName : '',
            accountNo: paymentMode === 'Bank Transfer' ? accountNo : '',
            branch: paymentMode === 'Bank Transfer' ? branch : '',
            ifsc: paymentMode === 'Bank Transfer' ? ifsc : '',
            updatedAt: serverTimestamp()
        };

        try {
            if (editId) {
                await updateDoc(doc(db, "salaryAllocations", editId), payload);
            } else {
                await addDoc(collection(db, "salaryAllocations"), { ...payload, createdAt: serverTimestamp() });
            }
            handleClose();
            await fetchAllocations();
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setSelectedStaff(null);
        setBasic(0);
        setHra(0);
        setDa(0);
        setTa(0);
        setBankName('');
        setAccountNo('');
        setBranch('');
        setIfsc('');
        setPaymentMode('Bank Transfer');
    };

    const startEdit = (row) => {
        setEditId(row.id);
        const stf = staffList.find(s => s.id === row.staffId);
        setSelectedStaff(stf || { id: row.staffId, label: row.staffName, name: row.staffName });
        setBasic(row.basic);
        setHra(row.hra);
        setDa(row.da);
        setTa(row.ta);
        setBankName(row.bankName || '');
        setAccountNo(row.accountNo || '');
        setBranch(row.branch || '');
        setIfsc(row.ifsc || '');
        setPaymentMode(row.paymentMode || 'Bank Transfer');
        setOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this salary allocation structure?")) {
            try {
                await deleteDoc(doc(db, "salaryAllocations", id));
                await fetchAllocations();
            } catch (error) {
                console.error("Error deleting allocation:", error);
            }
        }
    };

    const filteredAllocations = useMemo(() => {
        return allocations.filter(a => (a.staffName?.toLowerCase() || "").includes(searchTerm.toLowerCase()));
    }, [allocations, searchTerm]);

    const paginatedAllocations = useMemo(() => {
        return filteredAllocations.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    }, [filteredAllocations, page]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress size={35} />
            </Box>
        );
    }

    return (
        <Box p={1} bgcolor="#f0f2f5" minHeight="100vh">
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxWidth: 1200, margin: '0 auto' }}>
                {/* Header Action Row */}
                <Box display="flex" justifyContent="between" alignItems="center" justifyItems="space-between" mb={2.5} flexWrap="wrap" gap={2}>
                    <Box flexGrow={1}>
                        <Typography variant="h6" fontWeight={700} color="textPrimary">
                            Salary & Payment Allocation
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Manage baseline structure mappings and payment destinations
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpen(true)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Allocate Salary
                    </Button>
                </Box>

                {/* Filter Row */}
                <TextField
                    size="small"
                    placeholder="Search staff record..."
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    sx={{ mb: 2, maxWidth: 350 }}
                    InputProps={{
                        startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />

                {/* Compact Data Table Matrix */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell width="80px"><b>Index</b></TableCell>
                                <TableCell><b>Staff Member</b></TableCell>
                                <TableCell><b>Basic Salary</b></TableCell>
                                <TableCell><b>Gross Compensation</b></TableCell>
                                <TableCell><b>Distribution Mode</b></TableCell>
                                <TableCell align="right" width="120px"><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedAllocations.map((row, i) => (
                                <TableRow key={row.id} hover sx={{ '& td': { py: 0.8 } }}>
                                    <TableCell>{(page - 1) * itemsPerPage + i + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{row.staffName}</TableCell>
                                    <TableCell>₹{row.basic?.toLocaleString('en-IN')}</TableCell>
                                    <TableCell sx={{ color: '#1e88e5', fontWeight: 700 }}>₹{row.totalSalary?.toLocaleString('en-IN')}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ bgcolor: '#e8eaf6', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600, color: '#3f51b5' }}>
                                            {row.paymentMode}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => startEdit(row)}>
                                            <EditIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                            <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredAllocations.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No structural parameters configured yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredAllocations.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            size="small"
                            count={Math.ceil(filteredAllocations.length / itemsPerPage)}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                        />
                    </Box>
                )}
            </Card>

            {/* Structured Dialog Popup Window */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 1 }}>
                    {editId ? 'Modify Compensation Parameters' : 'Register New Allocation Rule'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers sx={{ py: 2 }}>
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-6 mb-2" item xs={12} sm={7}>
                                <Autocomplete
                                    options={staffList}
                                    getOptionLabel={(option) => option.label || ""}
                                    value={selectedStaff}
                                    onChange={(e, val) => setSelectedStaff(val)}
                                    renderInput={(params) => <TextField {...params} label="Target Staff Member" size="small" required />}
                                />
                            </Grid>
                            <Grid className="col-sm-6 mb-2" item xs={12} sm={5}>
                                <TextField select fullWidth label="Distribution Variant" size="small" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Cheque">Cheque</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid className="col-sm-12 mb-2" item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary" fontWeight={700}>COMPENSATION PARAMETERS</Typography>
                                </Divider>
                            </Grid>

                            <Grid className="col-sm-6 mb-2" item xs={6} sm={3}><TextField fullWidth label="Base Wage" type="number" size="small" required value={basic} onChange={(e) => setBasic(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-2" item xs={6} sm={3}><TextField fullWidth label="HRA Allow." type="number" size="small" value={hra} onChange={(e) => setHra(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-2" item xs={6} sm={3}><TextField fullWidth label="DA Allow." type="number" size="small" value={da} onChange={(e) => setDa(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-2" item xs={6} sm={3}><TextField fullWidth label="TA Allow." type="number" size="small" value={ta} onChange={(e) => setTa(e.target.value)} /></Grid>

                            <Grid className="col-sm-12 mb-2" item xs={12}>
                                <Box p={1.5} bgcolor="#e3f2fd" borderRadius={2} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="textSecondary" fontWeight={600}>Calculated Gross Return:</Typography>
                                    <Typography variant="subtitle1" fontWeight={800} color="#0d47a1">₹{totalSalary.toLocaleString('en-IN')}</Typography>
                                </Box>
                            </Grid>

                            {paymentMode === 'Bank Transfer' && (
                                <>
                                    <Grid className="col-sm-12 mb-2" item xs={12}>
                                        <Divider sx={{ my: 0.5 }}>
                                            <Typography variant="caption" color="textSecondary" fontWeight={700}>BANK TRANSACTION MATRIX</Typography>
                                        </Divider>
                                    </Grid>
                                    <Grid className="col-sm-6 mb-2" item xs={12} sm={6}><TextField fullWidth label=" Designation" size="small"  value={bankName} onChange={(e) => setBankName(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-2" item xs={12} sm={6}><TextField fullWidth label="Account Numeric String" size="small"  value={accountNo} onChange={(e) => setAccountNo(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-2" item xs={12} sm={6}><TextField fullWidth label="Branch Settlement Node" size="small" value={branch} onChange={(e) => setBranch(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-2" item xs={12} sm={6}><TextField fullWidth label="IFSC Code Routing" size="small"  value={ifsc} onChange={(e) => setIfsc(e.target.value)} /></Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                        <Button size="small" onClick={handleClose} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button size="small" type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                            {saving ? 'Processing...' : 'Save Configuration'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default PaymentAllocation;